import { IconMinus, IconPlus } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useMemo } from 'react';
import {
    Input, InputGroup, InputGroupAddon, InputGroupText,
} from 'reactstrap';
import 'css/partials/_bricks.NSInputNumberStepper.scss';
import { buildSteps } from './interestOnlyPeriodStepperHelpers';

export interface IInterestOnlyPeriodStepperProps {
    loanFundingMonth: number;
    saleMonth: number;
    value: number | null;
    onChange: (months: number | null) => void;
    inputGroupClassName?: string;
}

const InterestOnlyPeriodStepper = ({
    loanFundingMonth,
    saleMonth,
    value,
    onChange,
    inputGroupClassName,
}: IInterestOnlyPeriodStepperProps) => {
    const loanTermMonths = saleMonth - loanFundingMonth;

    const steps = useMemo(() => buildSteps(loanTermMonths), [loanTermMonths]);

    const currentIndex = useMemo(() => {
        if (value === null) return steps.length - 1;
        const idx = steps.findIndex(s => s.months === value);
        return idx >= 0 ? idx : 0;
    }, [value, steps]);

    const isMinDisabled = currentIndex <= 0;
    const isMaxDisabled = currentIndex >= steps.length - 1;

    const handleMinus = () => {
        if (isMinDisabled) return;
        const nextStep = steps[currentIndex - 1];
        onChange(nextStep.months);
    };

    const handlePlus = () => {
        if (isMaxDisabled) return;
        const nextStep = steps[currentIndex + 1];
        onChange(nextStep.months);
    };

    const displayLabel = steps[currentIndex]?.label ?? '0 years';

    return (
        <InputGroup className={`d-flex NSInputNumberStepper ${inputGroupClassName ?? ''}`}>
            <InputGroupAddon addonType="prepend" className="NSInputNumberStepper__addon-prepend cursor--pointer">
                <InputGroupText
                    tag="button"
                    type="button"
                    className={`py-0 NSInputNumberStepper__addon-prepend__text ${isMinDisabled ? 'disabled' : ''}`}
                    onClick={handleMinus}
                    data-testid="ioPeriodMinus"
                    aria-label="Decrease interest-only period"
                    disabled={isMinDisabled}
                >
                    <NSIcon icon={IconMinus} />
                </InputGroupText>
            </InputGroupAddon>
            <Input
                type="text"
                name="interestOnlyPeriodMonths"
                aria-label="interestOnlyPeriodMonths"
                value={displayLabel}
                className="NSInputNumberStepper__input border-right-0 border-left-0 text-center"
                readOnly
            />
            <InputGroupAddon addonType="append" className="NSInputNumberStepper__addon-append cursor--pointer">
                <InputGroupText
                    tag="button"
                    type="button"
                    className={`py-0 NSInputNumberStepper__addon-append__text ${isMaxDisabled ? 'disabled' : ''}`}
                    onClick={handlePlus}
                    data-testid="ioPeriodPlus"
                    aria-label="Increase interest-only period"
                    disabled={isMaxDisabled}
                >
                    <NSIcon icon={IconPlus} />
                </InputGroupText>
            </InputGroupAddon>
        </InputGroup>
    );
};

export default InterestOnlyPeriodStepper;
